import type { RecipeFamily, RecipeFieldSpec } from "./recipeTypes";

// learning-core config 의 큐레이션 필드 카탈로그(ACT + diffusion 계열). "자주 바꾸고 의미 있는" leaf 만
// 담는다. 인터폴레이션 파생·아키텍처 내부 leaf(core_module 인코더 내부·mux·buffer·image_transforms·
// vector_transforms·sampling.*.components·dataset 의 ${...} 미러)는 의도적으로 제외한다. 그런 값은
// raw YAML 로만 편집한다. enum 은 config 코멘트로 확증된 값만 하드코딩한다. 미확증 값(optimizer·
// lr_scheduler·device·diffusion prediction_type/beta_schedule/inference_type)은 string 으로 두어 422 를 피한다.

// 코멘트로 확증된 enum 옵션.
export const POLICY_TYPE_OPTIONS = ["DirectPolicy", "DiffusionPolicy"] as const;
export const CORE_MODULE_TYPE_OPTIONS = [
  "ActionTransformer",
  "DiffusionActionTransformer",
] as const;
export const AMP_DTYPE_OPTIONS = ["fp16", "bf16"] as const;
export const LOSS_TYPE_OPTIONS = ["l1_loss", "mse_loss"] as const;
export const ROTATION_REPRESENTATION_OPTIONS = ["quaternion", "SO3", "reduced-SO3"] as const;
export const SAMPLING_TYPE_OPTIONS = ["uniform_time", "squashed_time", "arc_length"] as const;
export const DATASET_PREDICTION_TYPE_OPTIONS = ["action", "status"] as const;
export const COMPILE_MODE_OPTIONS = ["default", "max-autotune", "reduce-overhead"] as const;
export const RAMP_TYPE_OPTIONS = ["linear", "cosine", "sigmoid", "exponential"] as const;
export const PREDECODE_BACKEND_OPTIONS = ["torchcodec", "ffmpeg"] as const;

const DIFFUSION: readonly RecipeFamily[] = ["diffusion"];

export const RECIPE_FIELD_CATALOG: readonly RecipeFieldSpec[] = [
  // ── policy ────────────────────────────────────────────────────────────────
  {
    path: "policy.policy_type",
    section: "policy",
    label: "Policy 종류",
    type: "enum",
    enumOptions: POLICY_TYPE_OPTIONS,
    readOnly: true,
    help: "계열 마커. 변경은 raw YAML 에서.",
  },
  {
    path: "policy.core_module.type",
    section: "policy",
    label: "코어 모듈",
    type: "enum",
    enumOptions: CORE_MODULE_TYPE_OPTIONS,
    readOnly: true,
    help: "계열 감지에 쓰인다(ActionTransformer=ACT, DiffusionActionTransformer=diffusion).",
  },
  {
    path: "policy.n_features",
    section: "policy",
    label: "특징 차원(n_features)",
    type: "integer",
    help: "여러 ${policy.n_features} 참조의 근원. 서버에서 재해석된다.",
  },
  {
    path: "policy.seed",
    section: "policy",
    label: "시드",
    type: "string",
    readOnly: true,
    help: "인터폴레이션 ${resolve_seed:...}. 바꾸려면 raw YAML.",
  },
  {
    path: "policy.vision_encoder.params.model_name",
    section: "policy",
    label: "비전 인코더 Model",
    type: "string",
    help: "timm Model 이름(예: vit_small_patch14_reg4_dinov2).",
  },
  {
    path: "policy.vision_encoder.params.pretrained",
    section: "policy",
    label: "비전 인코더 사전Training 사용",
    type: "boolean",
  },
  {
    path: "policy.vision_encoder.params.freeze",
    section: "policy",
    label: "비전 인코더 동결",
    type: "boolean",
  },
  {
    path: "policy.vision_encoder.share_weights",
    section: "policy",
    label: "비전 인코더 Weights 공유",
    type: "boolean",
  },
  {
    path: "policy.normalizer.apply_normalization_to_rotation",
    section: "policy",
    label: "회전값 정규화 적용",
    type: "boolean",
  },
  {
    path: "policy.status_prediction.enable",
    section: "policy",
    label: "상태 예측 사용",
    type: "boolean",
  },
  {
    path: "policy.status_prediction.loss_weight",
    section: "policy",
    label: "상태 예측 손실 가중치",
    type: "number",
  },
  {
    path: "policy.status_prediction.focal_gamma",
    section: "policy",
    label: "상태 예측 focal gamma",
    type: "number",
  },
  {
    path: "policy.status_prediction.soft_label.enable",
    section: "policy",
    label: "소프트 라벨 사용",
    type: "boolean",
  },
  {
    path: "policy.status_prediction.soft_label.window",
    section: "policy",
    label: "소프트 라벨 윈도우(초)",
    type: "number",
  },
  {
    path: "policy.status_prediction.soft_label.ramp_type",
    section: "policy",
    label: "소프트 라벨 램프 형태",
    type: "enum",
    enumOptions: RAMP_TYPE_OPTIONS,
  },
  { path: "policy.compile.enable", section: "policy", label: "컴파일 사용", type: "boolean" },
  {
    path: "policy.compile.mode",
    section: "policy",
    label: "컴파일 모드",
    type: "enum",
    enumOptions: COMPILE_MODE_OPTIONS,
  },
  {
    path: "policy.compile.fullgraph",
    section: "policy",
    label: "컴파일 fullgraph",
    type: "boolean",
  },
  // diffusion 전용 core_module 스칼라.
  {
    path: "policy.core_module.params.n_training_steps",
    section: "policy",
    label: "확산 Training 스텝 수",
    type: "integer",
    families: DIFFUSION,
  },
  {
    path: "policy.core_module.params.n_inference_steps",
    section: "policy",
    label: "확산 Inference 스텝 수",
    type: "integer",
    families: DIFFUSION,
  },
  {
    path: "policy.core_module.params.inference_type",
    section: "policy",
    label: "확산 Inference 방식",
    type: "string",
    families: DIFFUSION,
    help: "예: deterministic. 허용값 미확증이라 자유 입력.",
  },
  {
    path: "policy.core_module.params.prediction_type",
    section: "policy",
    label: "확산 예측 타깃",
    type: "string",
    families: DIFFUSION,
    help: "예: epsilon. 허용값 미확증이라 자유 입력.",
  },
  {
    path: "policy.core_module.params.beta_schedule",
    section: "policy",
    label: "확산 beta 스케줄",
    type: "string",
    families: DIFFUSION,
    help: "예: squaredcos_cap_v2. 허용값 미확증이라 자유 입력.",
  },
  {
    path: "policy.core_module.params.snr_loss_weighting",
    section: "policy",
    label: "SNR 손실 가중치 사용",
    type: "boolean",
    families: DIFFUSION,
  },
  {
    path: "policy.core_module.params.snr_loss_weighting_gamma",
    section: "policy",
    label: "SNR 손실 gamma",
    type: "integer",
    families: DIFFUSION,
  },

  // ── dataset ───────────────────────────────────────────────────────────────
  {
    path: "dataset.path",
    section: "dataset",
    label: "Dataset 경로",
    type: "string",
    readOnly: true,
    help: "인터폴레이션 ${get_absolute_path:...}. 제출 시 플랫폼이 주입한다.",
  },
  {
    path: "dataset.dataset_marker",
    section: "dataset",
    label: "Dataset 마커",
    type: "list",
    nullable: true,
    help: "재귀 탐색용 마커 목록. 없으면 null.",
  },
  {
    path: "dataset.target_dataset_index",
    section: "dataset",
    label: "타깃 Dataset 인덱스",
    type: "integer",
    nullable: true,
    help: "다중 Dataset에서 에폭 길이 기준. null 이면 전체 균등.",
  },
  {
    path: "dataset.max_decoder_handles",
    section: "dataset",
    label: "최대 디코더 핸들",
    type: "integer",
    help: "캐싱 비디오 디코더 수. 0 이면 캐시 비활성.",
  },
  {
    path: "dataset.rotation_representation",
    section: "dataset",
    label: "회전 표현",
    type: "enum",
    enumOptions: ROTATION_REPRESENTATION_OPTIONS,
  },
  {
    path: "dataset.prediction_type",
    section: "dataset",
    label: "데이터 로딩 방식",
    type: "enum",
    enumOptions: DATASET_PREDICTION_TYPE_OPTIONS,
    help: "action=원격조작, status=Direct Teaching 취급.",
  },
  { path: "dataset.validation_split", section: "dataset", label: "검증 분할 비율", type: "number" },
  {
    path: "dataset.dataset_stats_path",
    section: "dataset",
    label: "Dataset 통계 경로",
    type: "string",
    nullable: true,
  },
  {
    path: "dataset.sampling.type",
    section: "dataset",
    label: "샘플링 방식",
    type: "enum",
    enumOptions: SAMPLING_TYPE_OPTIONS,
  },
  {
    path: "dataset.predecoded.enable",
    section: "dataset",
    label: "사전 디코드 캐시 사용",
    type: "boolean",
  },
  {
    path: "dataset.predecoded.backend",
    section: "dataset",
    label: "사전 디코드 백엔드",
    type: "enum",
    enumOptions: PREDECODE_BACKEND_OPTIONS,
  },
  {
    path: "dataset.predecoded.num_workers",
    section: "dataset",
    label: "사전 디코드 워커 수",
    type: "integer",
    nullable: true,
  },
  {
    path: "dataset.predecoded.max_disk_gb",
    section: "dataset",
    label: "사전 디코드 최대 디스크(GB)",
    type: "number",
    nullable: true,
  },
  {
    path: "dataset.random_background_cache.enable",
    section: "dataset",
    label: "랜덤 배경 캐시 사용",
    type: "boolean",
  },

  // ── train ─────────────────────────────────────────────────────────────────
  { path: "train.batch_size", section: "train", label: "배치 크기", type: "integer" },
  { path: "train.num_epochs", section: "train", label: "에폭 수", type: "integer" },
  { path: "train.num_workers", section: "train", label: "워커 수", type: "integer" },
  { path: "train.num_val_workers", section: "train", label: "검증 워커 수", type: "integer" },
  {
    path: "train.save_interval",
    section: "train",
    label: "Checkpoint 저장 간격(에폭)",
    type: "integer",
  },
  {
    path: "train.seed",
    section: "train",
    label: "Training 시드",
    type: "string",
    readOnly: true,
    help: "인터폴레이션 ${policy.seed}. 바꾸려면 raw YAML.",
  },
  { path: "train.deterministic", section: "train", label: "결정론적 실행", type: "boolean" },
  {
    path: "train.device",
    section: "train",
    label: "디바이스",
    type: "string",
    help: "예: cuda. 허용값 미확증이라 자유 입력.",
  },
  {
    path: "train.loss_type",
    section: "train",
    label: "손실 함수",
    type: "enum",
    enumOptions: LOSS_TYPE_OPTIONS,
    help: "ACT 기본 l1_loss, diffusion 기본 mse_loss.",
  },
  {
    path: "train.per_timestep_loss.enable",
    section: "train",
    label: "타임스텝별 손실 사용",
    type: "boolean",
    help: "diffusion 에서는 false 권장.",
  },
  {
    path: "train.optimizer",
    section: "train",
    label: "옵티마이저",
    type: "string",
    help: "예: AdamW. 허용값 미확증이라 자유 입력.",
  },
  {
    path: "train.optimizer_params.default.lr",
    section: "train",
    label: "학습률(lr)",
    type: "number",
    help: "지수표기 지원(예: 1e-5).",
  },
  {
    path: "train.optimizer_params.default.weight_decay",
    section: "train",
    label: "weight decay",
    type: "number",
  },
  {
    path: "train.optimizer_params.default.betas",
    section: "train",
    label: "Adam betas",
    type: "list",
    help: "예: [0.95, 0.999].",
  },
  {
    path: "train.optimizer_params.default.eps",
    section: "train",
    label: "옵티마이저 eps",
    type: "number",
  },
  { path: "train.grad_clip_norm", section: "train", label: "그래디언트 클립 norm", type: "number" },
  {
    path: "train.lr_scheduler",
    section: "train",
    label: "LR 스케줄러",
    type: "string",
    help: "예: CosineAnnealingWithLinearWarmup. 허용값 미확증이라 자유 입력.",
  },
  {
    path: "train.lr_scheduler_params.num_warmup_epochs",
    section: "train",
    label: "warmup 에폭 수",
    type: "integer",
  },
  {
    path: "train.lr_scheduler_params.eta_min",
    section: "train",
    label: "LR 하한(eta_min)",
    type: "number",
  },
  { path: "train.use_amp", section: "train", label: "AMP 사용", type: "boolean" },
  {
    path: "train.amp_dtype",
    section: "train",
    label: "AMP dtype",
    type: "enum",
    enumOptions: AMP_DTYPE_OPTIONS,
  },
  { path: "train.cross_validate", section: "train", label: "교차 검증", type: "boolean" },
  {
    path: "train.augmentation_schedule.enable",
    section: "train",
    label: "증강 스케줄 사용",
    type: "boolean",
  },
  {
    path: "train.co_train_schedule.enable",
    section: "train",
    label: "co-train 스케줄 사용",
    type: "boolean",
  },
  { path: "train.use_mlflow", section: "train", label: "MLflow 사용", type: "boolean" },
  { path: "train.log_freq", section: "train", label: "로그 간격(스텝)", type: "integer" },
];

// 해당 계열에 유효한 필드만 고른다. families 가 없으면 공유(양 계열) 필드이고, 있으면 해당 계열 필드다.
export function catalogForFamily(family: RecipeFamily): readonly RecipeFieldSpec[] {
  return RECIPE_FIELD_CATALOG.filter(
    (spec) => spec.families === undefined || spec.families.includes(family),
  );
}
